import {
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { Loader2, UploadCloud } from 'lucide-react';
import clsx from 'clsx';

/** Oito direções de partida, cobrindo o cartão — valores fixos, nunca
 * sorteados: o pré-render e o primeiro render do cliente têm de bater. */
const PARTICULAS = [
  { px: '-150px', py: '-45px' },
  { px: '140px', py: '-60px' },
  { px: '-90px', py: '55px' },
  { px: '170px', py: '40px' },
  { px: '-190px', py: '-10px' },
  { px: '60px', py: '-70px' },
  { px: '-40px', py: '70px' },
  { px: '200px', py: '-30px' },
] as const;

interface Props {
  desabilitado: boolean;
  onArquivo: (arquivo: File) => void;
}

/**
 * Área de arrastar-e-soltar + seletor de arquivo, para o único `.docx` da
 * auditoria pública.
 *
 * O `<input type="file">` fica sempre no DOM (só oculto) — é ele quem abre o
 * seletor nativo do sistema operacional; um `<div>` sozinho não consegue.
 * `desabilitado` cobre o tempo de `enviando`: impede um segundo arquivo
 * (arrastado ou clicado) enquanto o primeiro ainda está no ar.
 */
export function ZonaEnvio({ desabilitado, onArquivo }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastandoSobre, setArrastandoSobre] = useState(false);

  function abrirSeletor(): void {
    if (!desabilitado) inputRef.current?.click();
  }

  function aoSoltar(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setArrastandoSobre(false);
    if (desabilitado) return;
    const arquivo = evento.dataTransfer.files[0];
    if (arquivo) onArquivo(arquivo);
  }

  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>): void {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      abrirSeletor();
    }
  }

  return (
    <div
      role="button"
      tabIndex={desabilitado ? -1 : 0}
      aria-disabled={desabilitado}
      aria-busy={desabilitado}
      onClick={abrirSeletor}
      onKeyDown={aoTeclar}
      onDragOver={(e) => {
        e.preventDefault();
        if (!desabilitado) setArrastandoSobre(true);
      }}
      onDragLeave={() => setArrastandoSobre(false)}
      onDrop={aoSoltar}
      /*
        O estado sai do `className` e vira ATRIBUTO. São três — parado, com
        arquivo pairando, e auditando — e cada um ganha um gesto próprio em
        `.zona-envio` (`estilos/motion.css`): o cartão cresce ao receber o
        arquivo e respira enquanto audita, que é o único momento em que a
        pessoa espera sem saber quanto falta.
      */
      data-estado={desabilitado ? 'enviando' : arrastandoSobre ? 'pairando' : 'parado'}
      className={clsx(
        'zona-envio rounded-auth-card relative flex cursor-pointer flex-col items-center gap-3 border-2 border-dashed p-10 text-center',
        desabilitado && 'cursor-not-allowed opacity-60',
        arrastandoSobre
          ? 'border-auth-marca-hover bg-auth-marca/10'
          : 'border-auth-borda/15 hover:border-auth-borda/30 hover:bg-auth-superficie/[0.03]',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".docx"
        className="sr-only"
        disabled={desabilitado}
        onChange={(e) => {
          const arquivo = e.target.files?.[0];
          e.target.value = '';
          if (arquivo) onArquivo(arquivo);
        }}
      />

      {/*
        As partículas do processamento — direções fixas escritas aqui (nada
        sorteado: pré-render e cliente têm de bater). Só o estado `enviando`
        as anima; ver `.zona-envio__particulas` em `estilos/motion.css`.
      */}
      <span aria-hidden className="zona-envio__particulas">
        {PARTICULAS.map((p, i) => (
          <i
            key={p.px + p.py}
            style={
              {
                '--px': p.px,
                '--py': p.py,
                '--atraso-particula': `${String(i * 190)}ms`,
              } as CSSProperties
            }
          />
        ))}
      </span>

      {desabilitado ? (
        <Loader2 size={28} className="text-auth-marca-hover animate-spin" aria-hidden />
      ) : (
        <UploadCloud size={28} className="text-auth-marca-hover" aria-hidden />
      )}

      <p className="text-auth-texto text-sm font-medium">
        {desabilitado
          ? 'Auditando o contrato…'
          : 'Arraste o .docx aqui, ou clique para escolher'}
      </p>
      <p className="text-auth-texto-terciario text-xs">
        {/* "2 MB" é copy — espelha `AUDITORIA_MAX_BYTES` (`.env.example`), a
            trava de verdade é sempre a da API, não esta frase. */}
        Só .docx, até 2 MB. O arquivo não é salvo em lugar nenhum.
      </p>
    </div>
  );
}
